import { faInbox } from '@fortawesome/pro-light-svg-icons';
import { Icon, Modal } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { ReactNode, useState } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';
import GridList from '~/components/GridList';
import { AmountOffOrderForm } from './AmountOffOrderForm';
import { AmountOffProductForm } from './AmountOffProductForm';

type DiscountKindOption = {
  kind: StoreDiscountKind;
  label: string;
  description: string;
  icon: ReactNode;
};

const discountKindOptions: DiscountKindOption[] = [
  {
    kind: 'amountOffProduct',
    label: 'Amount off products',
    description: 'Discount specific collections of products',
    icon: <Icon icon="tag" aria-hidden="true" />,
  },
  {
    kind: 'amountOffOrder',
    label: 'Amount off order',
    description: 'Discount the total order amount',
    icon: <Icon icon={faInbox} />,
  },
];

type StoreDiscountKind = z.infer<
  typeof CrewMerchantUi.discountDefinitionSchema
>['parameters']['kind'];

// Thin dispatcher: each discount `kind` has its own narrowly-typed form. This
// preserves the existing caller API (`kind` + `onClose`) while letting each
// branch own its defaults, schema, and kind-specific fields without union
// gymnastics.
export function DiscountDefinitionSettingsForm({
  show,
  onClose,
}: {
  onClose: () => void;
  show: boolean;
}) {
  const [kind, setSelectedKind] = useState<StoreDiscountKind | null>(null);

  const handleClose = () => {
    setSelectedKind(null);
    onClose();
  };

  const title = match(kind)
    .with('amountOffOrder', () => 'Create amount off order discount')
    .with('amountOffProduct', () => 'Create amount off product discount')
    .otherwise(() => 'Select discount type');

  return (
    <Modal title={title} show={show} onClose={handleClose}>
      {match(kind)
        .with('amountOffOrder', () => (
          <AmountOffOrderForm onClose={handleClose} />
        ))
        .with('amountOffProduct', () => (
          <AmountOffProductForm onClose={handleClose} />
        ))
        .otherwise(() => (
          <GridList columns={1} data={discountKindOptions}>
            {({ kind: buttonKind, label, description, icon }) => (
              <GridList.ItemButton
                key={buttonKind}
                label={label}
                description={description}
                icon={icon}
                secondaryIcon={<Icon icon="add" aria-hidden="true" />}
                variant="primary"
                onClick={() => setSelectedKind(buttonKind)}
              />
            )}
          </GridList>
        ))}
    </Modal>
  );
}
